import React, { FormEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import VelavuButton from "../../elements/velavu-button";
import VelavuInput from "../../elements/velavu-input";
import VelavuNotice from "../../elements/velavu-notice/velavu-notice";
import { checkEmail } from "../../helper/validation-helper";
import OnboardingFrame from "./onboarding-frame";
import { OnboardingFrameProps } from "./onboarding-shared-data";
import * as styles from "./onboarding-step-invite.module.scss";
import { VelavuAPI } from "velavu-js-api";

interface OnboardingStepInviteProps extends OnboardingFrameProps {
	organizationName: string;
}

export default function OnboardingStepInvite(props: OnboardingStepInviteProps) {
	const { t } = useTranslation();
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");

	const [userInvited, setUserInvited] = useState(false);
	const [isLoading, setLoading] = useState(false);

	const [result, setResult] = useState<
		{ success: boolean; description: string } | undefined
	>(undefined);

	const clearResult = useCallback(() => {
		if (result) setResult(undefined);
	}, [result, setResult]);
	const updateName = useCallback(
		(value: string) => {
			clearResult();
			setName(value);
		},
		[clearResult, setName],
	);
	const updateEmail = useCallback(
		(value: string) => {
			clearResult();
			setEmail(value);
		},
		[clearResult, setEmail],
	);

	const submit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			setLoading(true);

			VelavuAPI.users
				.registerNewUser({
					name: name,
					email: email,
				})
				.then(() => {
					setResult({
						success: true,
						description: t("onboarding.invite.invited", { email }),
					});
					setName("");
					setEmail("");
					setUserInvited(true);
				})
				.catch((error) => {
					console.log(error.response);

					if (error.response?.status === 409) {
						setResult({
							success: false,
							description: t("onboarding.invite.already_member", {
								email,
							}),
						});
					} else {
						setResult({
							success: false,
							description: t("onboarding.invite.invite_failed", {
								email,
							}),
						});
					}
				})
				.finally(() => {
					setLoading(false);
				});
		},
		[
			name,
			email,
			setResult,
			setName,
			setEmail,
			setLoading,
			setUserInvited,
			t,
		],
	);

	return (
		<OnboardingFrame
			title={t("onboarding.invite.title")}
			description={t("onboarding.invite.description")}
			buttonSecondary={{
				label: t("onboarding.invite.back"),
				onClick: props.navigateBackwards,
			}}
			buttonPrimary={{
				label: userInvited
					? t("onboarding.invite.next")
					: t("onboarding.invite.skip"),
				onClick: props.navigateForwards,
			}}
			notice={t("onboarding.invite.notice")}
		>
			<form onSubmit={submit}>
				<VelavuInput
					value={name}
					onChange={updateName}
					onFocus={clearResult}
					titleText={t("onboarding.invite.name")}
					autoComplete="name"
					fullWidth
					bottomPadding
				/>
				<VelavuInput
					value={email}
					onChange={updateEmail}
					onFocus={clearResult}
					titleText={t("onboarding.invite.email")}
					autoComplete="email"
					fullWidth
				/>
				<div className={styles.submitBar}>
					{result ? (
						<VelavuNotice
							type={result.success ? "confirm" : "error"}
						>
							{result.description}
						</VelavuNotice>
					) : (
						<VelavuButton
							className={styles.submitButton}
							label={t("onboarding.invite.send")}
							disabled={
								isLoading ||
								name.length === 0 ||
								!checkEmail(email)
							}
							submit
							fullWidth
						/>
					)}
				</div>
			</form>
		</OnboardingFrame>
	);
}
